<template>
  <div class="ToolPickerHUD">
    <div class="ToolPickerHUD_Tools" v-on:touchstart.stop>
      <div
        class="CircleShadowButton m-previous-page"
        v-on:click="previousPage"
        v-if="shouldShowPreviousPageButton"
      >
        <img src="/img/icons/caret-back-outline-white.svg" />
      </div>

      <div
        v-for="tool in toolsOnThisPage"
        v-bind:key="tool.toolType"
        v-bind:class="toolClass(tool)"
        v-on:click="setTool(tool)"
      >
        <img v-bind:src="tool.icon" v-bind:style="tool.style" />
        <div
          :class="{
            CircleShadowButton_HoverText: true,
            'm-right': true,
          }"
        >
          <span
            >{{ tool.label }}
            <span v-if="tool.hotkey">({{ tool.hotkey }})</span></span
          >
        </div>
      </div>

      <div class="ToolPickerHUD_Spacing MobileOnly"></div>
      <div
        class="CircleShadowButton m-next-page"
        v-on:click="nextPage"
        v-if="shouldShowNextPageButton"
      >
        <img src="/img/icons/caret-forward-outline-white.svg" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop } from "vue-property-decorator";
import { State } from "vuex-class";

import { ModalType } from "@/store/ModalType";
import { ToolHUDItem } from "../toolHUDItems";

import ToolType from "@/wbcontroller/types/ToolType";
import { UserInfo } from "@/wbcontroller/types/UserInfo";
import WhiteboardConfig from "@/WhiteboardConfig";
import WBComponent from "@/WBComponent";
import { ModalMutations } from "@/store/modules/modal";
import { GlobalMutations } from "@/store/mutations";

function getNumControlsPerPage(): number {
  if (window.innerHeight >= 768 && window.innerWidth >= 550) {
    return 1000;
  } else {
    return Math.floor(window.innerWidth / 50) - 1;
  }
}

@Component({})
export default class ToolPickerHUD extends WBComponent {
  @Prop() private config!: WhiteboardConfig;

  numControlsPerPage = getNumControlsPerPage();
  toolPage = 0;
  @State activeUsers!: Record<string, UserInfo>;

  mounted() {
    window.addEventListener("resize", this.onResize);
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  }

  onResize() {
    this.numControlsPerPage = getNumControlsPerPage();
    if (this.numControlsPerPage >= this.tools.length + 1) {
      this.toolPage = 0;
    }
  }

  get toolsOnThisPage(): ToolHUDItem[] {
    if (this.toolPage === 0) {
      return this.tools.slice(0, this.numControlsPerPage);
    } else {
      return this.tools.slice(this.numControlsPerPage);
    }
  }

  get shouldShowNextPageButton(): boolean {
    if (this.numControlsPerPage >= this.tools.length + 1) return false;
    return this.toolPage === 0;
  }

  get shouldShowPreviousPageButton(): boolean {
    if (this.numControlsPerPage >= this.tools.length + 1) return false;
    return this.toolPage === 1;
  }

  get hasActiveUsers(): boolean {
    return Object.keys(this.activeUsers).length > 0;
  }

  get tools(): ToolHUDItem[] {
    return this.$store.getters.tools;
  }

  get strokeWidth(): number {
    return this.state.hud.strokeWidth;
  }

  get colorBoxStyle(): string {
    return `background-color: ${this.state.hud.strokeColor}`;
  }

  get strokeWidthCircleStyle(): string {
    return `
      width: ${this.state.hud.strokeWidth}px;
      height: ${this.state.hud.strokeWidth}px;
      border-radius: ${this.state.hud.strokeWidth / 2}px;`;
  }

  get isGuest(): boolean {
    return this.config.session?.is_guest;
  }

  get usernameClass(): string {
    return this.isGuest ? "ClickableUsername" : "";
  }

  nextPage() {
    this.toolPage += 1;
  }

  previousPage() {
    this.toolPage -= 1;
  }

  toolClass(t: ToolHUDItem): string {
    const c =
      this.state.hud.tool === t.toolType
        ? "js-ToolButton CircleShadowButton m-active"
        : "js-ToolButton CircleShadowButton";
    // if (t.toolType === "pan") c += "MobileOnly";
    return `${c} m-${t.toolType} `;
  }

  setTool = (t: ToolHUDItem) => {
    switch (t.toolType) {
      case ToolType.ImageLibrary:
        ModalMutations.openModal(this.$store.commit, ModalType.ImageLibrary);
        break;
      case ToolType.ImageUpload:
        ModalMutations.openModal(this.$store.commit, ModalType.ImageUpload);
        break;
      default:
        GlobalMutations.setTool(this.$store.commit, t.toolType);
        break;
    }
  };

  openStrokeWidthPicker() {
    this.$emit("openStrokeWidthPicker");
  }

  openStrokeColorPicker() {
    this.$emit("openStrokeColorPicker");
  }

  deleteSelection(e: UIEvent) {
    e.stopPropagation();
    this.$emit("deleteSelection");
  }

  undo() {
    this.$emit("undo");
  }

  redo() {
    this.$emit("redo");
  }

  @Emit()
  editname() {
    /* do nothing */
  }
}
</script>

<style scoped lang="scss">
.ToolPickerHUD {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  justify-content: center;

  width: 100%;
  height: 50px;
  top: auto;
  right: 0;

  @media (min-height: 768px) and (min-width: 550px) {
    & {
      width: 50px;
      height: auto;
      top: 100px;
      right: auto;
    }

    .ToolPickerHUD_Tools {
      flex-direction: column;
      position: relative;
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
    }
  }
}

.ToolPickerHUD_Tools {
  background-color: var(--bg-dark);

  display: flex;
  overflow: visible;

  & > * {
    margin: 5px;
  }
}

.ToolPickerHUD_Spacing {
  flex-grow: 1;
}

.CircleShadowButton.m-next-page,
.CircleShadowButton.m-previous-page {
  background-color: transparent;
}

.ColorBox {
  width: 40px;
  height: 40px;
  border: 2px solid var(--bg-dark);
  cursor: pointer;

  &:hover {
    border: 2px solid white;
  }
}

.StrokeWidthIndicator {
  width: 40px;
  height: 40px;
  border: 2px solid var(--bg-dark);
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--highlight);
  }
}

.StrokeWidthIndicator_Circle {
  background-color: black;
}

.ClickableUsername {
  cursor: pointer;
}
</style>
